import { DatePipe } from '@angular/common';
import { Component,  OnDestroy,  OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ConfirmationModalComponent } from '../../../../modules/shared/components/confirmation-modal/confirmation-modal.component';
import { ClientConfiguration, ConfigurationService } from '../../../../services/configuration.service';
import { Event, EventPackage, EventRequestAllocation, EventRequestAllocationFacility, EventService } from '../../../../services/event.service';

@Component({
  selector: 'app-event-request-modal',
  templateUrl: './event-request-modal.component.html',
  styleUrls: ['./event-request-modal.component.scss']
})
export class EventRequestModalComponent implements OnInit, OnDestroy {

  private configuration: ClientConfiguration;

  @ViewChild('modal', { static: false }) private modalContent: TemplateRef<EventRequestModalComponent>;
  private modalRef: NgbModalRef;

  @ViewChild('confirmationModal', { static: false }) private confirmationModal: ConfirmationModalComponent;

  mode: ModalMode;

  minRequestDate: Date = new Date();
  maxRequestDate: Date = new Date();
  minAllocationDate: Date = new Date();
  maxAllocationDate: Date = new Date();

  selectedEventPackage: EventPackage;
  selectedEventRequest: EventRequestAllocation;

  selectedEventRequestAllocationFacilities: EventRequestAllocationFacility[];

  unsubscribe: Subject<any> = new Subject();


  constructor(
    private configService: ConfigurationService,
    private modalService: NgbModal,
    private eventsService: EventService,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this.configService.clientConfiguration
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(configuration => {
        this.configuration = configuration;
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onSubmit() {
    if (this.mode === ModalMode.Request) {
      this.saveRequest();
    } else if (this.mode === ModalMode.Allocate) {
      this.saveAllocation();
    }
  }

  saveRequest() {
    this.eventsService.createEventRequest(this.selectedEventRequest, this.selectedEventRequestAllocationFacilities, this.selectedEventPackage);
    this.close();
  }

  saveAllocation() {
    this.eventsService.allocateEventRequest(this.selectedEventRequest, this.selectedEventRequestAllocationFacilities, this.selectedEventPackage);
    this.close();
  }


  open(mode: ModalMode, selectedEventPackage: EventPackage, selectedEventRequest: EventRequestAllocation, selectedEvent: Event): Promise<any> {
    this.mode = mode;
    const rightNow = new Date();
    const minRequestDateStart = new Date(selectedEvent.eventStartDate || rightNow);
    const minAllocationDateStart = new Date(selectedEventPackage.requestStartDate || minRequestDateStart);
    this.minRequestDate = new Date(minRequestDateStart.getFullYear(), minRequestDateStart.getMonth(), minRequestDateStart.getDate(), 0, 0, 0, 0);
    this.maxRequestDate = new Date(rightNow.getFullYear(), rightNow.getMonth(), rightNow.getDate() + 7, 0, 0, 0, 0);
    this.minAllocationDate = new Date(minAllocationDateStart.getFullYear(), minAllocationDateStart.getMonth(), minAllocationDateStart.getDate(), 0, 0, 0, 0);
    this.maxAllocationDate = new Date(rightNow.getFullYear(), rightNow.getMonth(), rightNow.getDate() + 7, 0, 0, 0, 0);
    this.selectedEventPackage = selectedEventPackage;
    this.selectedEventRequest = selectedEventRequest;
    if (!!this.selectedEventRequest.eventRequestId) {
      this.eventsService.getEventRequestAllocationFacilities(this.selectedEventRequest.eventRequestId, this.selectedEventRequest.eventAllocationId);
    } else {
      this.eventsService.getAvailableRequestAllocationFacilities(this.selectedEventPackage.permitAnnualOperatingPlanId);
    }
    this.eventsService.eventRequestFacilities
      .pipe(takeUntil(this.unsubscribe),
        tap(eventRequestFacilities => {
          if (!!eventRequestFacilities && this.mode === ModalMode.Allocate) {
            eventRequestFacilities.forEach(x => {
              x.dnrApproved = x.appropriatorRequested;
            })
          }
        })
      )
      .subscribe(eventRequestFacilities => {
        if (eventRequestFacilities) {
          this.selectedEventRequestAllocationFacilities = eventRequestFacilities;
        }
      });
    this.modalRef = this.modalService.open(this.modalContent, { ariaLabelledBy: 'modal-basic-title', size: 'xl' });
    return this.modalRef.result;
  }

  async close() {
    console.log('EventRequestModal closed');
    this.modalRef.close(true);
  }

  async dismiss() {
    console.log('EventRequestModal dismissed');
    this.modalRef.dismiss('X');
  }

  requestFieldsEditable(): boolean {
    return this.mode === ModalMode.Request;
  }
  allocateFieldsEditable(): boolean {
    return this.mode === ModalMode.Allocate;
  }
  viewOnly(): boolean {
    return this.mode === ModalMode.ViewOnly;
  }
  submitButtonText(): string {
    if (this.requestFieldsEditable()) {
      return 'Request';
    } else if (this.allocateFieldsEditable()) {
      return 'Allocate';
    } else {
      return '';
    }
  }

  openConfirmationModal() {
    this.confirmationModal.open();
  }

  generateNoticeData(): string {
    if (!this.selectedEventPackage || !this.selectedEventRequest || !this.selectedEventRequestAllocationFacilities) return "";
    const formattedDate = !!this.selectedEventRequest.allocationDate ? this.datePipe.transform(this.selectedEventRequest.allocationDate, 'MM/dd/yyyy') : null;
    const approvedFacilities = this.selectedEventRequestAllocationFacilities.filter((item) => item.dnrApproved).map((item) => item.facilityName).join(", ");
    const noticeData = `The excess flow request for ${this.selectedEventPackage.appropriator}/${this.selectedEventPackage.diversion} is approved in the amount indicated below.
Allocated notice amount is ${this.selectedEventRequest.allocationAmount}.
This notice goes into effect on ${formattedDate}.
The following facilities are included in the request: ${approvedFacilities}.

${this.selectedEventRequest.allocationComment || ""}
`;
    return noticeData;
  }

  externalNoticeUrl(): string {
    return `${this.configuration.noticeUrlPrefix}${this.selectedEventRequest.externalNoticeId}${this.configuration.noticeUrlPostfix}`;
  }
  createNoticeUrl(): string {
    return `${this.configuration.createNoticeUrl}`;
  }
}


export enum ModalMode {
  ViewOnly = 0,
  Request = 1,
  Allocate = 2
}
